import React, { ReactNode, useCallback, useContext } from 'react';
import intersection from 'lodash/intersection';

import { GetSessionQuery as GetSession, Permission } from '__generated__/graphql';
import { gql } from '__generated__';

import useQuery from '../../api/useQuery';

export const GetSessionQuery = gql(`
  query GetSession {
    session {
      id
      user {
        id
        email
        first_name
        last_name
        locale
        last_project {
          id
          organisation_name
        }
        project_memberships {
          id
          permissions
          project {
            id
            organisation_name
            organisation_vat_number
            organisation_email
            organisation_address
            organisation_zip_code
            organisation_city
            organisation_country
            currency
            locale
            timezone
            genders
            verified
            feature_flags
          }
        }
      }
    }
  }
`);

type Session = GetSession['session'];
interface SessionContextValue {
  user?: Session['user'];
}

const initialSession = { user: {} } as SessionContextValue;
const SessionContext = React.createContext(initialSession);

export const useSession = () => {
  const { user } = useContext(SessionContext);

  /**
   * Indicates if the current user has any of the given permissions for the given project.
   */
  const hasPermission = useCallback((projectId: string, permissions: Permission[]) => (
    user && user.project_memberships.filter((membership) => (
      membership.project.id === projectId && intersection(membership.permissions, permissions).length > 0
    )).length > 0
  ), [user]);

  /**
   * Ordered list of permissions.
   */
  const permissions = [
    Permission.admin,
    Permission.event_dashboards,
    Permission.edit_events,
    Permission.view_registrations,
    Permission.edit_registrations,
    Permission.refunds,
    Permission.participant_logistics,
    Permission.exports,
    Permission.messages,
    Permission.financial_reports,
  ];

  return {
    user,
    hasPermission,
    permissions,
  };
};

interface SessionProviderProps {
  children: ReactNode;
  session?: Session;
  /**
   * Indicates whether the component should wait for the server response before rendering children.
   */
  renderImmediately?: boolean;
}

const SessionProvider = ({ renderImmediately = false, session: mockedSession, children }: SessionProviderProps) => {
  const { data } = useQuery(GetSessionQuery, {
    pollInterval: 10000,
    skip: !!mockedSession,
  });

  const session = mockedSession || data?.session || (renderImmediately ? initialSession : undefined);
  const user = session?.user;

  return (
    <SessionContext.Provider value={{ user }}>
      {session && children}
    </SessionContext.Provider>
  );
};

export default SessionProvider;
